








































































import {Vue, Component, Watch} from 'vue-property-decorator'
import FilterInvocation from '@/components/filters/FilterInvocation.vue'
import InvocationScrollableTable, {
  InvocationSortChangePayload,
} from '@/components/InvocationScrollableTable.vue'
import InvocationTooltip from '@/components/InvocationTooltip.vue'
import InvocationNetworkSelection from '@/components/InvocationNetworkSelection.vue'
import InvocationPeriodSelection from '@/components/InvocationPeriodSelection.vue'
import {$} from '@/facade'
import InvocationNetworkCards from '@/components/InvocationNetworkCards.vue'
import {ContractCollection} from '@/model/collection/ContractCollection'
import {DappCollection} from '@/model/collection/DappCollection'
import {Contract} from '@/model/resource/Contract'
import {Dapp} from '@/model/resource/Dapp'
import {BlockchainVersion} from '@/model/resource/BlockchainVersion'
import {BlockchainVersionCollection} from '@/model/collection/BlockchainVersionCollection'

@Component({
  components: {
    InvocationNetworkCards,
    InvocationPeriodSelection,
    InvocationNetworkSelection,
    InvocationTooltip,
    FilterInvocation,
    InvocationScrollableTable,
  },

  computed: {
    $() {
      return $
    },
  },
})
export default class InvocationDashboardView extends Vue {
  private readonly invocationListPageSize = 10

  contractCollection = new ContractCollection()
  dappCollection = new DappCollection()

  invocationContracts: Contract[] = []
  invocationDapps: Dapp[] = []

  isLoadingInvocationContracts = false
  isLoadingInvocationDapps = false

  groupOptions = ['contract', 'dApps']
  groupSelected = 'contract'
  networkOptions = ['n3', 'x']
  selectedNetwork = 'n3'
  periodOptions = ['Daily', 'Weekly', 'Bi-weekly', 'Monthly']
  selectedPeriod = 'Daily'

  blockchainVersionsForInvocation: BlockchainVersion[] = []

  get paginationCollection(): ContractCollection | DappCollection {
    return this.groupSelected === 'dApps'
      ? this.dappCollection
      : this.contractCollection
  }

  get paginationCurrentPage(): number {
    const c = this.paginationCollection
    return (c.currentPage ?? 0) + 1
  }

  get paginationTotalPages(): number {
    return this.paginationCollection.lastPage + 1
  }

  get paginationTotalCount(): number {
    return this.paginationCollection.total ?? 0
  }

  get paginationPerPage(): number {
    return this.paginationCollection.perPage ?? this.invocationListPageSize
  }

  handleSelectNetwork(network: string) {
    this.selectedNetwork = network
  }

  private partitionChainIds(network: 'n3' | 'x'): number[] {
    const n3: number[] = []
    const neoX: number[] = []
    for (const v of this.blockchainVersionsForInvocation) {
      const id = v.idBlockchainVersionPk
      if (!id) continue
      const ni = (v.networkIdentifier || '').toLowerCase()
      if (ni.startsWith('neox')) neoX.push(id)
      else n3.push(id)
    }
    return network === 'x' ? neoX : n3
  }

  private primaryBlockchainVersionId(network: 'n3' | 'x'): number | null {
    const versions = this.blockchainVersionsForInvocation
    const preferredIdentifier =
      network === 'x' ? 'neox/mainnet' : 'neo3/mainnet'
    const preferred = versions.find(
      v =>
        (v.networkIdentifier || '').toLowerCase() === preferredIdentifier &&
        v.idBlockchainVersionPk
    )
    if (preferred?.idBlockchainVersionPk) {
      return preferred.idBlockchainVersionPk
    }

    const ids = this.partitionChainIds(network)
    return ids[0] ?? null
  }

  private applyInvocationNetworkFilter() {
    const network = this.selectedNetwork === 'x' ? 'x' : 'n3'
    const versionId = this.primaryBlockchainVersionId(network)
    const versionFilter = versionId ? [versionId] : []

    this.contractCollection.idBlockchainVersionFk = [...versionFilter]
    this.dappCollection.idBlockchainVersionFk = [...versionFilter]
  }

  private async loadInvocationBlockchainVersions() {
    const coll = new BlockchainVersionCollection().noPagination()
    try {
      await coll.listBlockchainVersion()
      this.blockchainVersionsForInvocation = coll.items
    } catch {
      this.blockchainVersionsForInvocation = []
    }
  }

  @Watch('selectedNetwork')
  async onInvocationNetworkChanged() {
    this.applyInvocationNetworkFilter()
    if (this.groupSelected === 'dApps') {
      await this.loadFirstInvocationDappsPage()
    } else {
      await this.loadFirstInvocationContractsPage()
    }
  }

  handleSelectPeriod(period: string) {
    this.selectedPeriod = period
  }

  async handleSelectGroup(group: string) {
    this.groupSelected = group

    if (group === 'dApps') {
      await this.loadFirstInvocationDappsPage()
    } else {
      await this.loadFirstInvocationContractsPage()
    }
  }

  async mounted() {
    this.applyContractCollection()

    this.applyDappCollection()

    await this.loadInvocationBlockchainVersions()
    this.applyInvocationNetworkFilter()

    await this.loadFirstInvocationContractsPage()
  }

  @Watch('contractCollection.items', {deep: true})
  onContractItemsChange() {
    if (this.groupSelected === 'contract') {
      this.syncInvocationContractsFromCollection()
    }
  }

  @Watch('dappCollection.items', {deep: true})
  onDappItemsChange() {
    if (this.groupSelected === 'dApps') {
      this.syncInvocationDappsFromCollection()
    }
  }

  applyContractCollection() {
    this.contractCollection.perPage = this.invocationListPageSize
    this.contractCollection.limit = this.invocationListPageSize
    this.contractCollection.currentPage = 0
    this.contractCollection.orderBy = 'recentTransactionCount'
    this.contractCollection.asc = false
    this.contractCollection.interval = 1
    this.contractCollection.private = null
    this.contractCollection.active = null
  }

  applyDappCollection() {
    this.dappCollection.perPage = this.invocationListPageSize
    this.dappCollection.currentPage = 0
    this.dappCollection.orderBy = 'recentTransactionCount'
    this.dappCollection.asc = false
    this.dappCollection.isDapp = true
    this.dappCollection.inActiveDevelopment = true
    this.dappCollection.private = false
    this.dappCollection.interval = 1
    this.dappCollection.requestName = 'invocationDapps'
  }

  async loadFirstInvocationContractsPage() {
    this.contractCollection.currentPage = 0
    this.isLoadingInvocationContracts = true

    try {
      await this.$await.run('invocationContractsPage', async () => {
        await this.contractCollection.queryAsPage()
      })

      this.syncInvocationContractsFromCollection()
    } finally {
      this.isLoadingInvocationContracts = false
    }
  }

  async loadFirstInvocationDappsPage() {
    this.dappCollection.currentPage = 0
    this.isLoadingInvocationDapps = true

    try {
      await this.$await.run('invocationDappsPage', async () => {
        await this.dappCollection.queryAsPage()
      })

      this.syncInvocationDappsFromCollection()
    } finally {
      this.isLoadingInvocationDapps = false
    }
  }

  syncInvocationContractsFromCollection() {
    this.invocationContracts = [...this.contractCollection.items]
  }

  syncInvocationDappsFromCollection() {
    this.invocationDapps = [...this.dappCollection.items]
  }

  onInvocationPaginationChange() {
    if (this.groupSelected === 'contract') {
      this.syncInvocationContractsFromCollection()
    } else {
      this.syncInvocationDappsFromCollection()
    }
  }

  async onInvocationSortChange(payload: InvocationSortChangePayload) {
    if (this.groupSelected === 'dApps') {
      this.dappCollection.orderBy = payload.orderBy
      this.dappCollection.interval = payload.interval
      this.dappCollection.asc = payload.ascending
      await this.loadFirstInvocationDappsPage()
      return
    }

    this.contractCollection.orderBy = payload.orderBy
    this.contractCollection.interval = payload.interval
    this.contractCollection.asc = payload.ascending
    await this.loadFirstInvocationContractsPage()
  }
}
